import { Navigate, NavLink, Outlet, useLocation, useNavigate, useParams } from 'react-router';
import {
  Undo2,
  Redo2,
  Download,
  Wifi,
  WifiOff,
  Cloud,
  Coffee,
  Home,
  Users,
  ListOrdered,
  GitBranch,
  Play,
  Table2,
  LayoutGrid,
  Monitor,
  Printer,
  Settings,
  Menu,
  ChevronsLeft,
  ChevronsRight,
  ArrowLeft,
  Trophy,
  X,
  type LucideIcon,
} from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { TournamentProvider, useCompetition, useSync, useToasts, useTurnier, type Toast } from '@/store/TournamentProvider';
import { Button, Dialog, Meldung, Pill, cx } from '@/ui';
import { merkeHinweis, useGemerkterHinweis } from '@/lib/hinweis';
import { serializeBackup, backupFileName } from '@airmail/io';
import { downloadText } from '@/lib/download';
import { fmtTime } from '@/lib/format';
import { HelferTourButton, TourButton } from '@/tour/Tour';
import { offerHelferTour } from '@/tour/state';
import { EigenesLogo, Partner, useKontoBranding } from '@/components/Partner';
import { statusLabel } from '@/pages/TournamentList';
import { activePhases, estimatePhaseEnd, isPaused } from '@airmail/engine';
import { Link } from 'react-router';
import { useT } from '@/i18n';
import { useTm } from '@/i18n/engine';
import { Darstellung } from '@/components/Darstellung';

export function TournamentShell() {
  const { id } = useParams();
  return (
    <TournamentProvider id={id as string} fallback={<NotFound />}>
      <Shell />
    </TournamentProvider>
  );
}

function NotFound() {
  const t = useT();
  return (
    <div className="p-10">
      <h1 className="text-xl font-bold">{t('Turnier nicht gefunden')}</h1>
      <p className="text-muted">
        {t('Vielleicht gelöscht oder auf einem anderen Gerät angelegt.')}{' '}
        <Link className="text-blue underline" to="/">
          {t('Zur Turnierliste')}
        </Link>
      </p>
    </div>
  );
}

type Comp = ReturnType<typeof useCompetition>;

/** `helfer: true` = auch im eingeschränkten Helfer-Modus sichtbar (docs/notes/helfer.md) */
const NAV: { to: string; label: string; icon: LucideIcon; mobileOnly?: boolean; helfer?: boolean; show?: (c: Comp) => boolean; count?: (c: Comp) => string | number | undefined }[] = [
  // „Start“ nur auf dem Handy: die Einstiegsseite ersetzt dort die abstrakte Symbolleiste (docs/notes/start.md)
  { to: 'start', label: 'Start', icon: Home, mobileOnly: true, helfer: true },
  { to: 'teams', label: 'Teams', icon: Users, count: (c) => c.teams.filter((t) => t.status !== 'withdrawn').length },
  // Im Menü „Auslosung“ (Daniel, 09/2026): kürzer als „Gruppenphase“, und das ist, was man auf der Seite tut. Die Phase selbst heißt weiter Gruppenphase.
  { to: 'vorrunde', label: 'Auslosung', icon: ListOrdered, count: (c) => c.phases.find((p) => p.type === 'groups')?.groups.length || undefined },
  /*
   * Entweder Bracket oder Runde-für-Runde-KO (docs/notes/ko-runden.md) — nie
   * beides nebeneinander, und keins von beiden, wenn das Turnier gar keine
   * Endrunde hat. Im Liga-Modus und beim reinen „jeder gegen jeden" gibt es
   * keine KO-Phase (Harald: „Gruppenphase oder KO-Runde gibt's da nicht"), der
   * Punkt führte aber trotzdem auf eine leere Seite. Über Einstellungen →
   * Modus kommt die Phase zurück, falls doch eine gebraucht wird.
   */
  { to: 'ko', label: 'KO-Runde', icon: GitBranch, show: (c) => c.phases.some((p) => p.type === 'single_elim') },
  {
    to: 'ko-runden',
    label: 'KO-Runden',
    icon: GitBranch,
    show: (c) => c.phases.some((p) => p.type === 'ko_rounds'),
    count: (c) => c.phases.find((p) => p.type === 'ko_rounds')?.koRounds?.length || undefined,
  },
  { to: 'spiele', label: 'Spiele', icon: Play, helfer: true, count: (c) => c.phases.flatMap((p) => p.matches).filter((m) => m.status === 'running').length || undefined },
  { to: 'tabelle', label: 'Tabelle', icon: Table2, helfer: true },
  { to: 'felder', label: 'Felder', icon: LayoutGrid },
  { to: 'beamer', label: 'Beamer', icon: Monitor },
  { to: 'export', label: 'Export & Druck', icon: Printer },
  { to: 'einstellungen', label: 'Einstellungen', icon: Settings },
];

/** Seiten, die ein Helfer öffnen darf — alles andere wird auf „Spiele" umgeleitet */
const HELFER_SEITEN = ['start', 'spiele', 'tabelle'];
/**
 * Handy-Leiste unten: nur das, was am Turniertag mit dem Daumen erreichbar
 * sein muss (Daniel: „man braucht nur das was wirklich wichtig ist"). Alles
 * andere steckt hinter „Mehr“. Bewusst dieselben drei wie beim Helfer — die
 * Leitung sitzt am Laptop, am Handy ist sie in derselben Rolle wie ein Helfer.
 */
const UNTEN = ['start', 'spiele', 'tabelle'];

const SIDEBAR_KEY = 'airmail.sidebar';
function useSeitenleiste(): [boolean, () => void] {
  const [eingeklappt, setEingeklappt] = useState(() => {
    try {
      return localStorage.getItem(SIDEBAR_KEY) === 'zu';
    } catch {
      return false;
    }
  });
  const toggle = useCallback(() => {
    setEingeklappt((v) => {
      try {
        localStorage.setItem(SIDEBAR_KEY, v ? 'auf' : 'zu');
      } catch {
        /* egal */
      }
      return !v;
    });
  }, []);
  return [eingeklappt, toggle];
}

function Shell() {
  const { tournament, undo, canUndo, redo, canRedo, lastSavedAt } = useTurnier();
  const sync = useSync();
  const { toasts, dismissToast } = useToasts();
  const nav = useNavigate();
  // Helfer-Modus (docs/notes/helfer.md): nur Spiele und Tabelle, ggf. ein Ort
  const loc = useLocation();
  const helfer = sync.cfg?.role === 'helfer';
  const helferOrt = sync.cfg?.courtGroup;
  // Einem Helfer wird die kurze Einführung beim ersten Öffnen einmal angeboten (docs/notes/tour.md)
  useEffect(() => {
    if (helfer) offerHelferTour(tournament.id);
  }, [helfer, tournament.id]);
  const comp = useCompetition();
  const online = useOnline();
  const t = useT();
  // Toasts tragen den deutschen Engine-/Server-Text; übersetzt wird erst hier, damit ein Sprachwechsel nachwirkt
  const tm = useTm();
  const phase = activePhases(tournament)[0]?.phase;
  const eta = phase ? estimatePhaseEnd(tournament, phase.id, Date.now()) : undefined;
  const courtsCount = tournament.courts.filter((c) => c.active).length;
  const paused = isPaused(tournament, Date.now());
  const [eingeklappt, toggleSeitenleiste] = useSeitenleiste();
  const [mehrOffen, setMehrOffen] = useState(false);
  // Seitenwechsel schließt das Blatt — sonst stünde es nach dem Antippen eines Links noch da
  useEffect(() => {
    setMehrOffen(false);
  }, [loc.pathname]);

  // Hinweis vom Backup-Import auf der Turnierliste — erst hier, im Turnier, ist Zeit ihn zu lesen
  const gemerkt = useGemerkterHinweis();
  const importHinweis = gemerkt && (!gemerkt.turnier || gemerkt.turnier === tournament.id) ? gemerkt : null;
  const hinweisAuf = (x: Toast) => {
    if (x.to) nav(x.to);
    dismissToast(x.id);
  };

  const backup = () => downloadText(backupFileName(tournament), serializeBackup(tournament));
  const sichtbar = NAV.filter((n) => (!n.show || n.show(comp)) && (!helfer || n.helfer));
  const helferText = helferOrt ? t('Helfer · {ort}', { ort: helferOrt }) : t('Helfer');

  /*
   * Ein Punkt sagt, wie es dem Turnier geht — am Handy der einzige Platz dafür:
   * grau = nur auf diesem Gerät, grün = mit der Cloud synchron, gelb = wartet
   * (offline oder Eingaben offen), rot = Fehler. Antippen öffnet die Einzelheiten.
   */
  const ampel: 'grau' | 'gruen' | 'gelb' | 'rot' = !sync.cfg ? (online ? 'grau' : 'gelb') : sync.status === 'error' ? 'rot' : sync.status === 'idle' && online ? 'gruen' : 'gelb';
  const ampelText = !sync.cfg
    ? online
      ? t('Nur auf diesem Gerät gespeichert')
      : t('Offline — läuft weiter')
    : sync.status === 'idle'
      ? t('Cloud synchron')
      : sync.status === 'error'
        ? t('Cloud: Fehler')
        : sync.status === 'offline' || !online
          ? t('Cloud wartet auf Netz')
          : t('Cloud: {n} offen', { n: sync.pending });

  const statusZeilen = (
    <div className="font-mono text-[10.5px] text-dim leading-[1.7]">
      <div>
        <span className={cx('inline-block w-1.5 h-1.5 rounded-full mr-1.5', lastSavedAt ? 'bg-live' : 'bg-warn')} />
        {lastSavedAt ? t('gespeichert {t}', { t: fmtTime(lastSavedAt) }) : t('lokal gespeichert')}
      </div>
      <div className="flex items-center gap-1.5">
        {online ? <Wifi size={11} /> : <WifiOff size={11} />}
        {online ? t('online') : t('offline — läuft weiter')}
      </div>
      {sync.cfg && (
        <div className={cx('flex items-center gap-1.5', sync.status === 'idle' ? 'text-live' : sync.status === 'error' ? 'text-err' : 'text-warn')} title={sync.lastError ?? ''}>
          <Cloud size={11} />
          {sync.status === 'idle'
            ? `${t('cloud synchron')}${sync.lastSyncAt ? ' ' + fmtTime(sync.lastSyncAt) : ''}`
            : sync.status === 'syncing'
              ? t('cloud …')
              : sync.status === 'pending'
                ? t('cloud: {n} offen', { n: sync.pending })
                : sync.status === 'offline'
                  ? t('cloud wartet auf Netz')
                  : t('cloud: Fehler')}
        </div>
      )}
      {/* Welcher Stand läuft hier? Ohne die Zeile war das im Supportfall nicht feststellbar */}
      <div className="text-[10px] opacity-70" title={t('Version')}>
        v{__APP_VERSION__}
      </div>
    </div>
  );

  /*
   * Eigenes Logo (Pro, docs/21) ersetzt hier die Airmail-Marke — Daniel
   * (09/2026): neben der AIC unten „geht es verloren, macht die Leiste voll
   * und lenkt vom Lizenziert-Hinweis ab“. Oben ist der prominenteste Platz der
   * App; „Airmail“ bleibt als kleine Zeile darunter stehen. Eingeklappt wird
   * das Logo auf die Leistenbreite gebracht.
   */
  const eigenesLogo = useKontoBranding().logo;
  const marke = eigenesLogo ? (
    <span className={cx('grid gap-1 min-w-0', eingeklappt && 'justify-items-center')} data-marke-eigen>
      <EigenesLogo logo={eigenesLogo} hoehe={eingeklappt ? 28 : 44} className={cx('max-w-full', eingeklappt && '!max-w-[36px]')} />
      {!eingeklappt && <span className="label block text-[9.5px]">Airmail</span>}
    </span>
  ) : (
    <>
      <span className="w-[26px] h-[26px] bg-red relative shrink-0 [clip-path:polygon(16%_0,100%_0,84%_100%,0_100%)] after:content-[''] after:absolute after:left-[44%] after:top-[30%] after:w-[7px] after:h-[7px] after:rounded-full after:bg-side" />
      {!eingeklappt && (
        <span>
          <b className="block text-[15px] tracking-tight leading-tight">Airmail</b>
          <span className="label block text-[9.5px]">{t('Cornhole-Turniere')}</span>
        </span>
      )}
    </>
  );

  return (
    <div className={cx('grid grid-cols-[minmax(0,1fr)] min-h-full', eingeklappt ? 'md:grid-cols-[56px_minmax(0,1fr)]' : 'md:grid-cols-[208px_minmax(0,1fr)]')}>
      {/*
        Seitenleiste nur ab Laptop-Breite; ein- und ausklappbar (Daniel, 09/2026),
        gemerkt im Browser. Am Handy übernimmt die Leiste unten.
      */}
      <aside className="bg-side border-r border-line py-4 hidden md:flex flex-col sticky top-0 h-screen">
        {/*
          Für den Helfer ist das Zeichen kein Link: Über die Turnierliste käme
          er sonst an „Neues Turnier" — und genau das soll er nicht können
          (docs/notes/helfer.md).
        */}
        {helfer ? (
          <div className={cx('flex items-center gap-2.5 pb-4 text-ink', eingeklappt ? 'px-[15px]' : 'px-4')}>{marke}</div>
        ) : (
          <Link to="/" className={cx('flex items-center gap-2.5 pb-4 no-underline text-ink', eingeklappt ? 'px-[15px]' : 'px-4')} title={t('Zur Turnierliste')}>
            {marke}
          </Link>
        )}
        <nav className="flex flex-col" aria-label={t('Hauptnavigation')}>
          {sichtbar
            .filter((n) => !n.mobileOnly)
            .map((n) => (
              <NavItem key={n.to} to={n.to} label={t(n.label)} icon={n.icon} count={n.count?.(comp)} sep={n.to === 'export'} kompakt={eingeklappt} />
            ))}
        </nav>
        <div className="mt-auto" />
        {!eingeklappt && (
          <div className="px-4">
            {helfer && (
              /*
               * Kein „verlassen" mehr: Dieses Gerät hat einen Helfer-Schlüssel und
               * keinen Turnier-Key (docs/notes/helfer.md). Es gäbe nichts, wohin
               * man verlassen könnte — und der Knopf war genau das Schlupfloch,
               * über das man sich früher zur Leitung machte.
               */
              <div className="border border-blue text-blue px-2 py-1.5 mb-2">
                <span className="label leading-tight">{helferText}</span>
              </div>
            )}
            {/* Die große Einführung legt ein Demo-Turnier an und startet in der
                Turnierliste — für einen Helfer wäre das die Hintertür zu „Neues
                Turnier" (Daniel: „so geht es zurück aufs hauptmenu"). Er bekommt
                stattdessen eine kurze Tour im laufenden Turnier. */}
            {helfer ? <HelferTourButton size="sm" className="w-full mb-2" /> : <TourButton size="sm" className="w-full mb-2" />}
            {/* Sprache & Darstellung: ein Knopf, das Menü klappt nach oben — unten ist kein Platz */}
            <Darstellung oben links beschriftet className="mb-2" />
            {/* Verbandshinweis, klein und ruhig — kein Werbeplatz */}
            <div className="mb-2">
              <Partner />
            </div>
            {/*
              Status und Rechtliches stehen hier nicht mehr (Daniel, 09/2026: „wird
              ziemlich voll dort unten"): Den Zustand zeigt der Punkt oben rechts,
              die Einzelheiten stehen dahinter unter „Mehr"; Impressum, Datenschutz
              und AGB stehen unten in der Turnierliste und auf der Anmeldeseite.
              Nur die Version bleibt — im Supportfall die erste Frage.
            */}
            <div className="font-mono text-[10px] text-dim opacity-70 pt-1" title={t('Version')}>
              v{__APP_VERSION__}
            </div>
          </div>
        )}
        <button
          type="button"
          onClick={toggleSeitenleiste}
          aria-label={eingeklappt ? t('Seitenleiste ausklappen') : t('Seitenleiste einklappen')}
          title={eingeklappt ? t('Seitenleiste ausklappen') : t('Seitenleiste einklappen')}
          className={cx('mt-3 mx-2 flex items-center gap-2 px-2 py-1.5 text-xs text-dim hover:text-ink hover:bg-panel2 cursor-pointer', eingeklappt && 'justify-center')}
        >
          {eingeklappt ? <ChevronsRight size={16} /> : <ChevronsLeft size={16} />}
          {!eingeklappt && <span>{t('Einklappen')}</span>}
        </button>
      </aside>
      <div className="flex flex-col min-w-0">
        <header className="flex items-center gap-2 md:gap-3 px-3 md:px-5 py-2.5 border-b border-line bg-panel sticky top-0 z-10 min-w-0">
          {/* Am Handy gibt es keine Seitenleiste — dort steht das eigene Logo vor dem Turniernamen (Daniel, 09/2026) */}
          <EigenesLogo logo={eigenesLogo} hoehe={28} className="md:hidden shrink-0" />
          <h1 className="m-0 text-base tracking-tight truncate min-w-0">
            {tournament.name} <em className="not-italic text-muted font-normal hidden lg:inline">· {comp.name}</em>
          </h1>
          {helfer && (
            <span className="md:hidden">
              <Pill tone="blue">{helferText}</Pill>
            </span>
          )}
          <span className="hidden lg:inline-flex">
            <Pill>{phase ? t(phaseLabel(phase.type)) : t(statusLabel(tournament.status))}</Pill>
          </span>
          <span className="hidden xl:inline-flex">
            <Pill>
              {t('{n} Punkte', { n: comp.ruleSet.target })} · {comp.ruleSet.bust === 'off' ? t('kein Bust') : t('Bust {n}', { n: comp.ruleSet.bust })}
            </Pill>
          </span>
          {paused && (
            <Pill tone="warn">
              <Coffee size={11} className="inline mr-1 -mt-0.5" />
              {t('Pause bis {t}', { t: fmtTime(tournament.pausedUntil) })}
            </Pill>
          )}
          {eta && eta.runningMatches > 0 && (
            <span className="hidden sm:inline-flex">
              <Pill tone="live">{t('{n} Spiele laufen', { n: eta.runningMatches })}</Pill>
            </span>
          )}
          <div className="flex-1" />
          {eta?.endsAt && (
            <div className="text-right leading-tight hidden md:block">
              <b className="font-mono text-[15px] tracking-wide">~{fmtTime(eta.endsAt)}</b>
              <span className="label block text-[10px]">{t('{phase} fertig · {n} Felder', { phase: t(phaseLabel(phase?.type)), n: courtsCount })}</span>
            </div>
          )}
          {/*
           * „Rückgängig" gibt es für Helfer nicht (Daniel, 09/2026). Es ging
           * auch gar nicht: Der Server lässt `revert` nicht durch, und
           * „Rückgängig" nimmt ohnehin die **letzte Aktion des ganzen
           * Turniers** zurück — nicht die eigene letzte. Auf einem Turnier, auf
           * dem die Leitung und mehrere Helfer gleichzeitig eintragen, wäre das
           * fast nie die eigene. Ein Vertippen korrigiert der Helfer, indem er
           * das Spiel noch einmal öffnet und das richtige Ergebnis einträgt —
           * das darf er.
           *
           * Am Handy stehen die Knöpfe unter „Mehr“ — die Kopfzeile ist dort zu schmal.
           */}
          {!helfer && (
            <span data-tour="hdr-backup" className="hidden md:inline-flex gap-2">
              <Button size="sm" onClick={undo} disabled={!canUndo} title={t('Letzte Aktion rückgängig')}>
                <Undo2 size={14} /> {t('Rückgängig')}
              </Button>
              {/* Nur da, wenn es etwas zurückzuholen gibt — sonst steht ein toter Knopf herum */}
              {canRedo && (
                <Button size="sm" onClick={redo} title={t('Zurückgenommene Eingabe wieder anwenden')}>
                  <Redo2 size={14} /> {t('Wieder vorwärts')}
                </Button>
              )}
              <Button size="sm" onClick={backup} title={t('Turnier als JSON-Datei sichern')}>
                <Download size={14} /> {t('Backup')}
              </Button>
            </span>
          )}
          {/* 44 px zum Treffen; der Zustand steht nicht nur in der Farbe, sondern auch als Zeichen (✓ ! ×) — für Farbenblinde */}
          <button
            type="button"
            onClick={() => setMehrOffen(true)}
            className="inline-flex items-center justify-center w-11 h-11 -mr-2 cursor-pointer text-dim hover:text-ink"
            aria-label={`${t('Status')}: ${ampelText}`}
            title={ampelText}
            data-status={ampel}
          >
            <span
              aria-hidden="true"
              className={cx(
                'flex items-center justify-center w-4 h-4 rounded-full text-[11px] font-bold leading-none text-bg',
                ampel === 'gruen' ? 'bg-live' : ampel === 'gelb' ? 'bg-warn' : ampel === 'rot' ? 'bg-err' : 'bg-dim',
              )}
            >
              {ampel === 'gruen' ? '✓' : ampel === 'gelb' ? '!' : ampel === 'rot' ? '×' : ''}
            </span>
          </button>
        </header>
        {(!online || (sync.cfg && (sync.status === 'offline' || sync.status === 'error'))) && (
          <div className={cx('flex items-center gap-3 px-3 md:px-5 py-2 text-sm border-b', !online ? 'border-warn text-warn bg-warn/5' : 'border-err text-err bg-err/5')} role="status">
            <WifiOff size={14} className="shrink-0" />
            <span className="flex-1">
              {!online
                ? helfer
                  ? t('Kein Internet — {n} Eingaben warten auf diesem Gerät und gehen automatisch raus, sobald wieder Netz da ist. Du kannst weiter eintragen.', { n: sync.pending })
                  : sync.cfg
                    ? t('Kein Internet — {n} Änderungen warten und werden automatisch nachgereicht, sobald wieder Netz da ist. Sicherheitshalber jetzt ein Backup ziehen.', { n: sync.pending })
                    : t('Kein Internet — das Turnier liegt nur auf diesem Gerät. Sicherheitshalber ein Backup ziehen; die Cloud-Sicherung holt es nach, sobald Netz da ist.')
                : helfer
                  ? t(
                      'Keine Verbindung zum Turnier{e}. Eingetragene Ergebnisse bleiben auf diesem Gerät und gehen von selbst raus, sobald es wieder geht. Hält es an: bei der Turnierleitung melden.',
                      { e: sync.lastError ? `: ${tm(sync.lastError)}` : '' },
                    )
                  : t('Cloud-Sync meldet einen Fehler{e}. Bis das behoben ist: Backup ziehen.', { e: sync.lastError ? `: ${tm(sync.lastError)}` : '' })}
            </span>
            {/* Ein Helfer hat keinen Backup-Knopf — er wäre für ihn auch kein Rat */}
            {!helfer && (
              <Button size="sm" onClick={backup}>
                <Download size={14} /> {t('Backup')}
              </Button>
            )}
          </div>
        )}
        {/* Unten Platz für die Handy-Leiste, damit der letzte Knopf nicht darunter liegt */}
        <main className="p-3 md:p-5 pb-24 md:pb-5 flex-1 min-w-0">
          {/* Getippte oder gemerkte Adressen führen im Helfer-Modus nicht an der
              Navigation vorbei — sonst wäre das Ausblenden reine Dekoration. */}
          {importHinweis && (
            <Meldung ton={importHinweis.ton} onClose={() => merkeHinweis(null)} className="mb-3">
              {importHinweis.texte.map((x, i) => (i === 0 && importHinweis.werte ? t(x, importHinweis.werte) : tm(x))).join(' · ')}
            </Meldung>
          )}
          {helfer && !HELFER_SEITEN.some((seite) => loc.pathname.endsWith(`/${seite}`)) ? <Navigate to="spiele" replace /> : <Outlet />}
        </main>
      </div>

      {/* Handy: Leiste unten — Start, Spiele, Tabelle, Mehr */}
      <nav className="md:hidden fixed bottom-0 inset-x-0 z-20 bg-side border-t border-line grid grid-cols-4 pb-[env(safe-area-inset-bottom)]" aria-label={t('Hauptnavigation')}>
        {sichtbar
          .filter((n) => UNTEN.includes(n.to))
          .map((n) => (
            <UntenItem key={n.to} to={n.to} label={t(n.label)} icon={n.icon} count={n.count?.(comp)} />
          ))}
        <button
          type="button"
          onClick={() => setMehrOffen(true)}
          aria-expanded={mehrOffen}
          className="flex flex-col items-center justify-center gap-0.5 py-2 min-h-14 text-[10.5px] text-muted hover:text-ink cursor-pointer bg-transparent border-0 border-t-2 border-transparent"
        >
          <Menu size={20} />
          <span>{t('Mehr')}</span>
        </button>
      </nav>

      {/*
        „Mehr“: alles, was am Handy nicht in die Leiste passt — und am Laptop
        die Einzelheiten hinter dem Statuspunkt.
      */}
      <Dialog open={mehrOffen} onClose={() => setMehrOffen(false)} title={t('Mehr')} width="max-w-md">
        <div className="grid gap-4 text-sm">
          <div className="flex items-center gap-2">
            <span className={cx('block w-2.5 h-2.5 rounded-full shrink-0', ampel === 'gruen' ? 'bg-live' : ampel === 'gelb' ? 'bg-warn' : ampel === 'rot' ? 'bg-err' : 'bg-dim')} />
            <span>{ampelText}</span>
          </div>
          {statusZeilen}
          {/*
            Am Handy gibt es sonst keinen Weg aus dem Turnier heraus: Die
            Seitenleiste mit dem Zeichen als Link fehlt dort (Daniel, 09/2026:
            „keine Chance, zurück auf die Übersicht zu kommen"). Für den Helfer
            nicht — die Turnierliste wäre seine Hintertür zu „Neues Turnier".
          */}
          {!helfer && (
            <div className="md:hidden grid grid-cols-2 gap-2" data-mehr-zurueck>
              <Link to="/" className="flex items-center gap-2 px-3 py-2.5 border border-line no-underline text-ink min-h-11">
                <ArrowLeft size={17} className="shrink-0" />
                <span>{t('Turnierliste')}</span>
              </Link>
              <Link to="/saison" className="flex items-center gap-2 px-3 py-2.5 border border-line no-underline text-ink min-h-11">
                <Trophy size={17} className="shrink-0" />
                <span>{t('Saison')}</span>
              </Link>
            </div>
          )}
          {helfer && (
            <div className="md:hidden border border-blue text-blue px-2 py-1.5">
              <span className="label leading-tight">{helferText}</span>
            </div>
          )}
          <div className="md:hidden grid gap-1">
            {sichtbar
              .filter((n) => !UNTEN.includes(n.to))
              .map((n) => (
                <NavLink
                  key={n.to}
                  to={n.to}
                  className={({ isActive }) => cx('flex items-center gap-3 px-3 py-2.5 border no-underline min-h-11', isActive ? 'border-red text-ink bg-panel2 font-bold' : 'border-line text-muted')}
                >
                  <n.icon size={17} className="shrink-0" />
                  <span className="flex-1">{t(n.label)}</span>
                  {n.count?.(comp) !== undefined && <span className="font-mono text-[11px] text-dim">{n.count(comp)}</span>}
                </NavLink>
              ))}
          </div>
          {!helfer && (
            <div className="md:hidden flex gap-2 flex-wrap">
              <Button
                size="sm"
                onClick={() => {
                  undo();
                  setMehrOffen(false);
                }}
                disabled={!canUndo}
                title={t('Letzte Aktion rückgängig')}
              >
                <Undo2 size={14} /> {t('Rückgängig')}
              </Button>
              {canRedo && (
                <Button
                  size="sm"
                  onClick={() => {
                    redo();
                    setMehrOffen(false);
                  }}
                  title={t('Zurückgenommene Eingabe wieder anwenden')}
                >
                  <Redo2 size={14} /> {t('Wieder vorwärts')}
                </Button>
              )}
              <Button size="sm" onClick={backup} title={t('Turnier als JSON-Datei sichern')}>
                <Download size={14} /> {t('Backup')}
              </Button>
            </div>
          )}
          <div className="flex gap-2 flex-wrap items-center">{helfer ? <HelferTourButton size="sm" /> : <TourButton size="sm" />}</div>
          {/* Im Blatt „Mehr" stehen die drei Zeilen gleich da — ein Menü im Dialog wäre eines zu viel */}
          <Darstellung inline />
          <Partner />
        </div>
      </Dialog>

      {/*
       * Hinweise. Die beiden Bereiche stehen immer im DOM — nur dann sagen
       * Vorleseprogramme an, was hineinkommt (ein Live-Bereich, der erst mit
       * seinem Inhalt entsteht, wird oft verschluckt). Fehler assertiv, der
       * Rest höflich.
       *
       * Der Text selbst ist nur dann ein Knopf, wenn er irgendwohin führt
       * („… hier tippen"); sonst hieße „Rückgängig gemacht" für Tests und
       * Vorleseprogramme fast wie der Knopf „Rückgängig". Schließen geht immer
       * über das × — auch per Tastatur.
       */}
      <div className="fixed bottom-20 md:bottom-4 right-3 md:right-4 z-50 flex flex-col gap-2 max-w-[calc(100vw-1.5rem)] md:max-w-md pointer-events-none">
        <div role="alert" className="flex flex-col gap-2">
          {toasts
            .filter((x) => x.kind === 'error')
            .map((x) => (
              <Hinweis key={x.id} toast={x} text={tm(x.text)} onOpen={() => hinweisAuf(x)} onClose={() => dismissToast(x.id)} />
            ))}
        </div>
        <div role="status" className="flex flex-col gap-2">
          {toasts
            .filter((x) => x.kind !== 'error')
            .map((x) => (
              <Hinweis key={x.id} toast={x} text={tm(x.text)} onOpen={() => hinweisAuf(x)} onClose={() => dismissToast(x.id)} />
            ))}
        </div>
      </div>
    </div>
  );
}

/** Ein Hinweis unten rechts: Text (führt ggf. hin) und × zum Schließen */
function Hinweis({ toast: x, text, onOpen, onClose }: { toast: Toast; text: string; onOpen: () => void; onClose: () => void }) {
  const t = useT();
  return (
    <div
      data-hinweis={x.kind}
      className={cx(
        'pointer-events-auto flex items-start gap-1 border pl-4 pr-1 text-sm bg-panel shadow-lg',
        x.kind === 'error' ? 'border-err text-err' : x.kind === 'warn' ? 'border-warn text-warn' : 'border-live text-live',
      )}
    >
      {x.to ? (
        <button type="button" onClick={onOpen} className="flex-1 text-left py-2.5 bg-transparent border-0 p-0 text-inherit cursor-pointer underline decoration-dotted underline-offset-2">
          {text}
        </button>
      ) : (
        <span className="flex-1 py-2.5 cursor-pointer" onClick={onOpen}>
          {text}
        </span>
      )}
      <button
        type="button"
        onClick={onClose}
        aria-label={t('Hinweis schließen')}
        className="shrink-0 inline-flex items-center justify-center w-9 h-9 max-md:w-11 max-md:h-11 bg-transparent border-0 text-dim hover:text-ink cursor-pointer"
      >
        <X size={14} />
      </button>
    </div>
  );
}

function NavItem({ to, label, icon: Icon, count, sep, kompakt }: { to: string; label: string; icon: LucideIcon; count?: string | number | undefined; sep?: boolean; kompakt: boolean }) {
  return (
    <>
      {sep && <div className="h-px bg-line2 mx-4 my-2.5" />}
      <NavLink
        to={to}
        data-tour={`nav-${to}`}
        title={label}
        aria-label={label}
        className={({ isActive }) =>
          cx(
            'flex items-center gap-2.5 py-2 text-sm border-l-2 no-underline relative',
            kompakt ? 'px-[17px]' : 'px-4',
            isActive ? 'text-ink bg-panel2 border-red font-bold' : 'text-muted border-transparent hover:text-ink',
          )
        }
      >
        <Icon size={17} className={cx('shrink-0', !kompakt && 'hidden')} />
        {!kompakt && <span className="flex-1">{label}</span>}
        {count !== undefined && <span className={cx('font-mono text-[11px] text-dim', kompakt ? 'absolute right-1 top-1' : 'ml-auto')}>{count}</span>}
      </NavLink>
    </>
  );
}

function UntenItem({ to, label, icon: Icon, count }: { to: string; label: string; icon: LucideIcon; count?: string | number | undefined }) {
  return (
    <NavLink
      to={to}
      data-tour={`nav-${to}`}
      className={({ isActive }) =>
        cx(
          'flex flex-col items-center justify-center gap-0.5 py-2 min-h-14 text-[10.5px] no-underline relative border-t-2',
          isActive ? 'text-ink border-red font-bold' : 'text-muted border-transparent',
        )
      }
    >
      <Icon size={20} />
      <span>{label}</span>
      {count !== undefined && <span className="absolute right-[18%] top-1.5 font-mono text-[10px] text-dim">{count}</span>}
    </NavLink>
  );
}

export function phaseLabel(type: string | undefined): string {
  switch (type) {
    case 'groups':
      return 'Gruppenphase';
    case 'single_elim':
      return 'KO-Runde';
    case 'ko_rounds':
      return 'KO-Runden';
    default:
      return 'Phase';
  }
}

function useOnline(): boolean {
  const [online, setOnline] = useState(navigator.onLine);
  useEffect(() => {
    const on = () => setOnline(true);
    const off = () => setOnline(false);
    window.addEventListener('online', on);
    window.addEventListener('offline', off);
    return () => {
      window.removeEventListener('online', on);
      window.removeEventListener('offline', off);
    };
  }, []);
  return online;
}
